import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import { storeProfileAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const MAX_DISPLAY_W = 300;
const MAX_DISPLAY_H = 420;

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

export default function LabelTemplate({ onSaved }) {
  const { storeProfile, refreshStoreProfile } = useAuth();
  const saved = storeProfile?.label_template || null;

  const [stickerW, setStickerW] = useState(saved?.sticker_w_mm ?? 50);
  const [stickerH, setStickerH] = useState(saved?.sticker_h_mm ?? 75);
  const [zone, setZone] = useState({
    x_mm: saved?.zone_x_mm ?? 0,
    y_mm: saved?.zone_y_mm ?? 0,
    w_mm: saved?.zone_w_mm ?? 50,
    h_mm: saved?.zone_h_mm ?? 75,
  });

  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [dragging, setDragging]         = useState(false);
  const [dragStart, setDragStart]       = useState(null);
  const [dragRect,  setDragRect]        = useState(null);
  const [saving,    setSaving]          = useState(false);

  const containerRef = useRef(null);
  const fileRef      = useRef(null);

  // Compute display dimensions preserving aspect ratio
  const aspect     = stickerH / Math.max(stickerW, 0.1);
  let   displayW   = MAX_DISPLAY_W;
  let   displayH   = displayW * aspect;
  if (displayH > MAX_DISPLAY_H) { displayH = MAX_DISPLAY_H; displayW = displayH / aspect; }
  displayW = Math.round(displayW);
  displayH = Math.round(displayH);

  // mm ↔ pixel conversion for the display
  const scaleX = stickerW / displayW;   // mm per display-px
  const scaleY = stickerH / displayH;

  // Zone in display pixels (for the overlay rectangle)
  const overlayRect = {
    left:   zone.x_mm / scaleX,
    top:    zone.y_mm / scaleY,
    width:  zone.w_mm / scaleX,
    height: zone.h_mm / scaleY,
  };

  // ── File upload ──────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImageDataUrl(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Mouse drag helpers ───────────────────────────────────────────────────────
  const getPos = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const cx   = e.touches ? e.touches[0].clientX : e.clientX;
    const cy   = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clamp(cx - rect.left, 0, displayW),
      y: clamp(cy - rect.top,  0, displayH),
    };
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    setDragStart(getPos(e));
    setDragging(true);
    setDragRect(null);
  };

  const onMouseMove = useCallback((e) => {
    if (!dragging || !dragStart) return;
    const pos = getPos(e);
    setDragRect({
      left:   Math.min(dragStart.x, pos.x),
      top:    Math.min(dragStart.y, pos.y),
      width:  Math.abs(pos.x - dragStart.x),
      height: Math.abs(pos.y - dragStart.y),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, dragStart, displayW, displayH]);

  const onMouseUp = useCallback((e) => {
    if (!dragging || !dragStart) return;
    const pos = getPos(e);
    const px = Math.min(dragStart.x, pos.x);
    const py = Math.min(dragStart.y, pos.y);
    const pw = Math.abs(pos.x - dragStart.x);
    const ph = Math.abs(pos.y - dragStart.y);
    if (pw > 4 && ph > 4) {
      setZone({
        x_mm: parseFloat((px * scaleX).toFixed(1)),
        y_mm: parseFloat((py * scaleY).toFixed(1)),
        w_mm: parseFloat((pw * scaleX).toFixed(1)),
        h_mm: parseFloat((ph * scaleY).toFixed(1)),
      });
    }
    setDragging(false);
    setDragStart(null);
    setDragRect(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, dragStart, scaleX, scaleY]);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, [dragging, onMouseMove, onMouseUp]);

  // ── Save / clear ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (zone.w_mm <= 0 || zone.h_mm <= 0) {
      toast.error('Set the printable zone dimensions first');
      return;
    }
    setSaving(true);
    try {
      await storeProfileAPI.updateLabelTemplate({
        label_template: {
          sticker_w_mm: stickerW,
          sticker_h_mm: stickerH,
          zone_x_mm:    zone.x_mm,
          zone_y_mm:    zone.y_mm,
          zone_w_mm:    zone.w_mm,
          zone_h_mm:    zone.h_mm,
        },
      });
      await refreshStoreProfile();
      toast.success('Label template saved');
      onSaved?.();
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await storeProfileAPI.updateLabelTemplate({ label_template: null });
      await refreshStoreProfile();
      setImageDataUrl(null);
      setZone({ x_mm: 0, y_mm: 0, w_mm: stickerW, h_mm: stickerH });
      toast.success('Template cleared — printing full label design');
    } catch {
      toast.error('Failed to clear template');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full';

  return (
    <div className="space-y-4 pt-1">

      {/* Saved template summary */}
      {saved && (
        <div className="flex items-start gap-2 p-2.5 bg-primary/5 border border-primary/20 rounded-md text-xs">
          <Info className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <div>
            <span className="font-medium text-primary">Active template: </span>
            <span className="text-foreground">
              {saved.sticker_w_mm}×{saved.sticker_h_mm}mm sticker · print zone {saved.zone_w_mm}×{saved.zone_h_mm}mm
              at ({saved.zone_x_mm}, {saved.zone_y_mm})mm
            </span>
          </div>
        </div>
      )}

      {/* Sticker dimensions */}
      <div>
        <label className="block text-xs font-medium text-foreground mb-2">Sticker Size (mm)</label>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Width</label>
            <input type="number" min={10} max={300} value={stickerW}
              onChange={e => setStickerW(parseFloat(e.target.value) || 50)}
              className={inputCls} />
          </div>
          <span className="text-muted-foreground text-sm pb-2">×</span>
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Height</label>
            <input type="number" min={10} max={300} value={stickerH}
              onChange={e => setStickerH(parseFloat(e.target.value) || 75)}
              className={inputCls} />
          </div>
        </div>
      </div>

      {/* Image upload + zone selector */}
      <div>
        <label className="block text-xs font-medium text-foreground mb-2">
          Upload blank sticker photo &amp; drag to mark the printable area
        </label>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

        {!imageDataUrl ? (
          <button onClick={() => fileRef.current.click()}
            className="w-full border-2 border-dashed border-border rounded-lg py-5 text-center text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
            <Upload className="h-5 w-5 mx-auto mb-1.5" />
            Click to upload sticker photo
          </button>
        ) : (
          <div className="space-y-1">
            <div className="relative inline-block">
              {/* Image container — draggable zone selector */}
              <div
                ref={containerRef}
                style={{ width: displayW, height: displayH, cursor: 'crosshair', position: 'relative', userSelect: 'none' }}
                className="border border-border rounded overflow-hidden"
                onMouseDown={onMouseDown}
              >
                <img
                  src={imageDataUrl}
                  alt="Blank sticker"
                  style={{ width: displayW, height: displayH, display: 'block', pointerEvents: 'none' }}
                  draggable={false}
                />
                {/* Saved zone overlay */}
                <div style={{
                  position: 'absolute',
                  left:   overlayRect.left,   top:    overlayRect.top,
                  width:  overlayRect.width,  height: overlayRect.height,
                  border: '2px solid #0c7c4a',
                  background: 'rgba(12,124,74,0.18)',
                  pointerEvents: 'none',
                }} />
                {/* Live drag preview */}
                {dragging && dragRect && (
                  <div style={{
                    position: 'absolute',
                    left:   dragRect.left,   top:    dragRect.top,
                    width:  dragRect.width,  height: dragRect.height,
                    border: '2px dashed #0c7c4a',
                    background: 'rgba(12,124,74,0.10)',
                    pointerEvents: 'none',
                  }} />
                )}
              </div>
              {/* Remove image button */}
              <button onClick={() => setImageDataUrl(null)}
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5 shadow">
                <X className="h-3 w-3" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Drag on the image to mark the blank/printable zone (green box)</p>
          </div>
        )}
      </div>

      {/* Zone fine-tune inputs */}
      <div>
        <label className="block text-xs font-medium text-foreground mb-2">Printable Zone (mm) — fine-tune or enter manually</label>
        <div className="grid grid-cols-4 gap-2">
          {[['X offset', 'x_mm'], ['Y offset', 'y_mm'], ['Width', 'w_mm'], ['Height', 'h_mm']].map(([label, key]) => (
            <div key={key}>
              <label className="block text-xs text-muted-foreground mb-1">{label}</label>
              <input
                type="number" min={0} value={zone[key]}
                onChange={e => setZone(z => ({ ...z, [key]: parseFloat(e.target.value) || 0 }))}
                className={inputCls}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          X/Y = offset from top-left corner of sticker · Width/Height = printable area size
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : 'Save Template'}
        </button>
        {saved && (
          <button onClick={handleClear} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">
            <X className="h-4 w-4" /> Clear Template
          </button>
        )}
      </div>
    </div>
  );
}
